/**
 * Backend Historical Reports Hook
 * 
 * Updated hook following REACT_INTEGRATION.md patterns with:
 * - Backend API integration
 * - Proper error handling
 * - Loading states
 * - Authentication via authManager
 * - Data transformation
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
    HistoricalReportResponse,
    HistoricalReportDetail,
    ListReportsRequest,
    HistoricalReportListResponse
} from '../types/historical';
import { authenticatedHistoricalReportService } from '../services/historicalReportService';
import { authManager } from '../services/authManager';
import { reportListCache, reportDetailCache, requestDeduplicator, CacheKeys, CachePatterns, useCache } from '../utils/cache';
import {
    createHistoricalReportsError,
    retryWithBackoff,
    logHistoricalReportsError,
    HistoricalReportsError
} from '../utils/historicalReportsErrors';

interface UseBackendHistoricalReportsOptions {
    pageSize?: number;
    autoLoad?: boolean;
    initialFilters?: Partial<Omit<ListReportsRequest, 'user_email' | 'limit' | 'offset'>>;
}

interface UseBackendHistoricalReportsReturn {
    // Data
    reports: HistoricalReportResponse[];
    loading: boolean;
    error: HistoricalReportsError | null;

    // Pagination
    totalCount: number;
    limit: number;
    offset: number;
    hasMore: boolean;
    currentPage: number;
    totalPages: number;

    // Filters
    filters: Omit<ListReportsRequest, 'user_email' | 'limit' | 'offset'>;
    setFilter: <K extends keyof Omit<ListReportsRequest, 'user_email' | 'limit' | 'offset'>>(
        key: K,
        value: Omit<ListReportsRequest, 'user_email' | 'limit' | 'offset'>[K]
    ) => void;

    // Actions
    loadReports: () => Promise<void>;
    loadMore: () => Promise<void>;
    goToPage: (page: number) => Promise<void>;
    refresh: () => void;
    clearError: () => void;

    // Detail handling
    getReportDetail: (reportId: string) => Promise<HistoricalReportDetail | null>;
    detailCache: Map<string, HistoricalReportDetail>;
}

export function useBackendHistoricalReports(
    options: UseBackendHistoricalReportsOptions = {}
): UseBackendHistoricalReportsReturn {
    const { pageSize = 25, autoLoad = true, initialFilters = {} } = options;

    // State
    const [reports, setReports] = useState<HistoricalReportResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<HistoricalReportsError | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [offset, setOffset] = useState(0);
    const [filters, setFilters] = useState<Omit<ListReportsRequest, 'user_email' | 'limit' | 'offset'>>({
        order_by: 'generated_at',
        order_direction: 'desc',
        ...initialFilters,
    });

    // Refs
    const abortRef = useRef<AbortController | null>(null);
    const searchTimeoutRef = useRef<number | null>(null);
    const detailCache = useRef<Map<string, HistoricalReportDetail>>(new Map());
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Cache hooks
    const { get: getCachedList, set: setCachedList, invalidate } = useCache(reportListCache, {
        onCacheHit: () => { },
        onCacheMiss: () => { },
    });

    const { get: getCachedDetail, set: setCachedDetail } = useCache(reportDetailCache);

    // Computed
    const hasMore = useMemo(() => offset + reports.length < totalCount, [offset, reports.length, totalCount]);
    const currentPage = useMemo(() => Math.floor(offset / pageSize) + 1, [offset, pageSize]);
    const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / pageSize)), [totalCount, pageSize]);

    /**
     * Load reports with current filters and pagination
     */
    const loadReports = useCallback(async (append = false, explicitOffset?: number) => {
        const userEmail = authManager.getUserEmail();
        if (!userEmail) {
            const authError = createHistoricalReportsError('Not authenticated - please log in', 'authentication check');
            setError(authError);
            return;
        }

        const currentOffset = explicitOffset !== undefined ? explicitOffset : (append ? offset + reports.length : 0);
        const cacheKey = CacheKeys.reportsList(filters, currentOffset, pageSize);

        // Check cache first (only for non-refresh requests)
        if (!append && refreshTrigger === 0) {
            const cachedData = getCachedList(cacheKey);
            if (cachedData) {
                setReports(cachedData.reports);
                setTotalCount(cachedData.total_count);
                setOffset(0);
                setLoading(false);
                return;
            }
        }

        setLoading(true);
        if (!append) {
            setError(null);
        }

        // Cancel previous request
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const cacheKeyForDedup = `list:${userEmail}:${currentOffset}:${pageSize}:${JSON.stringify(filters)}`;

            const requestParams = {
                ...filters,
                limit: pageSize,
                offset: currentOffset,
            };

            const response = await requestDeduplicator.dedupe(cacheKeyForDedup, () =>
                retryWithBackoff(
                    () => authenticatedHistoricalReportService.listReports(requestParams),
                    3,
                    1000,
                    5000,
                )
            ) as HistoricalReportListResponse;

            // Cache the response
            setCachedList(cacheKey, response, 2 * 60 * 1000); // 2 minutes TTL

            if (!controller.signal.aborted) {
                if (append) {
                    setReports(prev => [...prev, ...response.reports]);
                } else {
                    setReports(response.reports);
                    setOffset(explicitOffset !== undefined ? explicitOffset : 0);
                }
                setTotalCount(response.total_count);
            }
        } catch (err) {
            if (!controller.signal.aborted) {
                const structuredError = createHistoricalReportsError(err, 'load historical reports');
                setError(structuredError);
                logHistoricalReportsError(structuredError, { filters, pageSize, append });
            }
        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
        }
    }, [filters, pageSize, offset, reports.length, getCachedList, setCachedList, refreshTrigger]);

    /**
     * Load more reports (for infinite scroll)
     */
    const loadMore = useCallback(async () => {
        if (loading || !hasMore) return;
        await loadReports(true);
    }, [loading, hasMore, loadReports]);

    /**
     * Go to a specific page
     */
    const goToPage = useCallback(async (page: number) => {
        if (loading) return;
        const clampedPage = Math.max(1, Math.min(page, totalPages));
        const newOffset = (clampedPage - 1) * pageSize;
        await loadReports(false, newOffset);
    }, [loading, totalPages, pageSize, loadReports]);

    /**
     * Set filter with debouncing for text fields
     */
    const setFilter = useCallback(<K extends keyof Omit<ListReportsRequest, 'user_email' | 'limit' | 'offset'>>(
        key: K,
        value: Omit<ListReportsRequest, 'user_email' | 'limit' | 'offset'>[K]
    ) => {
        // Reset pagination when filters change
        setOffset(0);

        // Debounce text searches (if we add search functionality later)
        if (key === 'data_object_id' && typeof value === 'string' && value.length > 0) {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            searchTimeoutRef.current = window.setTimeout(() => {
                setFilters(prev => ({ ...prev, [key]: value }));
            }, 300);
        } else {
            setFilters(prev => ({ ...prev, [key]: value }));
        }
    }, []);

    /**
     * Refresh data
     */
    const refresh = useCallback(() => {
        const userEmail = authManager.getUserEmail();
        if (userEmail) {
            // Invalidate related caches
            invalidate(CachePatterns.userReports(userEmail));
        }

        setOffset(0);
        setReports([]);
        setRefreshTrigger(prev => prev + 1);
        detailCache.current.clear();
    }, [invalidate]);

    /**
     * Clear current error
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * Get detailed report with caching
     */
    const getReportDetail = useCallback(async (reportId: string): Promise<HistoricalReportDetail | null> => {
        const cacheKey = CacheKeys.reportDetail(reportId);

        // Check cache first
        const cachedDetail = getCachedDetail(cacheKey);
        if (cachedDetail) {
            return cachedDetail;
        }

        // Also check legacy cache for backward compatibility
        if (detailCache.current.has(reportId)) {
            return detailCache.current.get(reportId)!;
        }

        try {
            const dedupKey = `detail:${reportId}`;
            const detail = await requestDeduplicator.dedupe(dedupKey, () =>
                retryWithBackoff(
                    () => authenticatedHistoricalReportService.getReportDetail(reportId),
                    3,
                    1000,
                    5000,
                    `get report detail ${reportId}`
                )
            ) as HistoricalReportDetail;

            // Cache in both systems for now
            setCachedDetail(cacheKey, detail, 10 * 60 * 1000); // 10 minutes TTL
            detailCache.current.set(reportId, detail);

            return detail;
        } catch (err) {
            const structuredError = createHistoricalReportsError(err, `get report detail ${reportId}`);
            setError(structuredError);
            logHistoricalReportsError(structuredError, { reportId });
            return null;
        }
    }, [getCachedDetail, setCachedDetail]);

    // Auto-load on mount and filter changes
    useEffect(() => {
        if (autoLoad) {
            // Defer loading until auth email is available to prevent noisy auth errors
            try {
                const email = authManager.getUserEmail();
                if (email) {
                    loadReports();
                }
            } catch {
                // Auth not ready yet; will re-run when filters/refreshTrigger changes AND component re-renders after auth sets email
            }
        }

        // Cleanup
        return () => {
            abortRef.current?.abort();
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [autoLoad, refreshTrigger, filters]);

    return {
        // Data
        reports,
        loading,
        error,

        // Pagination
        totalCount,
        limit: pageSize,
        offset,
        hasMore,
        currentPage,
        totalPages,

        // Filters
        filters,
        setFilter,

        // Actions
        loadReports: () => loadReports(false),
        loadMore,
        goToPage,
        refresh,
        clearError,

        // Detail handling
        getReportDetail,
        detailCache: detailCache.current,
    };
}

/**
 * Simplified hook for basic report listing
 */
export function useHistoricalReportsList(
    filters?: Partial<Omit<ListReportsRequest, 'user_email' | 'limit' | 'offset'>>,
    options?: UseBackendHistoricalReportsOptions
) {
    const hook = useBackendHistoricalReports(options);

    // Apply initial filters
    useEffect(() => {
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined) {
                    hook.setFilter(key as any, value);
                }
            });
        }
    }, []); // Only on mount

    return {
        reports: hook.reports,
        loading: hook.loading,
        error: hook.error,
        totalCount: hook.totalCount,
        hasMore: hook.hasMore,
        loadMore: hook.loadMore,
        refresh: hook.refresh,
    };
}